/* Navigation de page (republication forcée 2026-08-11) (personnalisations Ava Be) : bouton « Charger + » de
   la grille collection et flèche de retour en haut. Module autonome,
   chargé depuis layout/theme.liquid. */

/* « Charger + » : charge la page suivante de la pagination sans recharger
   la page — fetch de paginate.next.url, les cartes du même conteneur
   (attribut target) sont ajoutées à la grille courante, puis le bouton
   reprend l'URL suivante ou disparaît en dernière page. Sans JavaScript,
   le bouton est un simple lien rel="next" vers la page suivante. En cas
   d'erreur réseau, on retombe sur la navigation classique. */
if (!customElements.get('avb-load-more')) {
  class AvbLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-avb-load-more]');
      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        this._load();
      });
    }

    /* Le bouton devient un lien ordinaire vers la collection complète :
       même habillage, plus aucun chargement en place. La phrase de
       progression reste au-dessus — elle dit combien il en reste, le
       lien dit où les trouver. */
    _basculerVersTout() {
      const url = this.getAttribute('data-avb-tout-url');
      if (!url || !this.button) return;

      const lien = document.createElement('a');
      lien.className = this.button.className;
      lien.href = url;
      lien.textContent = this.getAttribute('data-avb-tout-libelle') || url;

      this.button.replaceWith(lien);
      this.button = null;
    }

    async _load() {
      if (!this.button) return;
      const url = this.button.getAttribute('href');
      const targetId = this.getAttribute('target');
      const target = document.getElementById(targetId);
      if (!url || !target || this.button.hasAttribute('aria-busy')) return;

      this.button.setAttribute('aria-busy', 'true');

      /* Dans l'application iOS, l'indicateur natif ne se déclenche que
         sur les chargements de page complets : un « Charger + » se ferait
         donc en silence. On le pilote nous-mêmes. Hors application,
         avbApp.spinner ne fait rien. */
      window.avbApp?.spinner(true);

      try {
        /* Sur une grosse collection, enchaîner les « Voir plus »
           déclenche la limitation de débit de Shopify : la réponse
           revient en 429. Une seule nouvelle tentative, après une
           courte pause, suffit presque toujours — la limitation se
           mesure en secondes. */
        let response = await fetch(url);
        if (response.status === 429 || response.status === 430) {
          await new Promise((tenir) => setTimeout(tenir, 1500));
          response = await fetch(url);
        }
        if (!response.ok) throw new Error(response.status);
        const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');

        const nextList = nextDocument.getElementById(targetId);
        if (!nextList) throw new Error('missing product list');

        /* LES 52 CARTES NE SONT PLUS INSÉRÉES D'UN SEUL COUP. Un appel
           unique à append() déclenche en une passe : le recalcul des
           requêtes de conteneur de 52 cartes, l'initialisation de 52
           carrousels, l'injection des pastilles et des cœurs par les
           applications tierces, et la sérialisation de tout cela par
           les enregistreurs de session (Clarity). Sur iPhone, cette
           avalanche unique finissait par tuer l'onglet — y compris sur
           un appareil récent, donc pas une affaire de mémoire mais de
           pointe de charge.

           On insère par poignées de 12, une image d'animation entre
           chaque : le travail total est le même, mais étalé sur
           quelques centaines de millisecondes, sous le seuil de
           saturation de tous les acteurs. À l'œil, rien ne change —
           les cartes arrivent sous la ligne de flottaison. */
        const enfants = Array.from(nextList.children);
        for (let debut = 0; debut < enfants.length; debut += 12) {
          target.append(...enfants.slice(debut, debut + 12));
          await new Promise((suite) => requestAnimationFrame(() => setTimeout(suite, 80)));
        }

        this._alleger(target);

        this._refreshProgress(target);

        /* Le bouton de retour en haut n'a pas à surveiller la grille pour
           savoir qu'une page a été chargée : on le lui dit. Compter les
           cartes à sa place le rendait dépendant d'un sélecteur qui ne le
           concerne pas. */
        document.dispatchEvent(new CustomEvent('avb:load-more', { detail: { target } }));

        /* PLAFOND DE RECHARGES. Là où l'attribut data-avb-max-charges
           est posé (la suite de collection des pages publicitaires), on
           s'arrête après N recharges : l'accumulation de cartes au-delà
           finit par faire rendre l'onglet à Safari sur iPhone — c'est
           la leçon, chèrement payée, du 2026-08-11. Le bouton devient
           alors un lien vers la collection complète, dont la
           pagination numérotée prend le relais sans limite. */
        this._charges = (this._charges || 0) + 1;
        const plafond = parseInt(this.getAttribute('data-avb-max-charges'), 10);
        if (plafond && this._charges >= plafond) {
          this._basculerVersTout();
          return;
        }

        const nextButton = nextDocument.querySelector('avb-load-more [data-avb-load-more]');
        if (nextButton) {
          this.button.setAttribute('href', nextButton.getAttribute('href'));
          this.button.removeAttribute('aria-busy');
        } else {
          /* Dernière page : le bouton disparaît, mais la phrase de
             progression reste — « vous avez vu 116 produits sur 116 »
             est une fin de liste explicite, là où un bouton qui
             s'évapore laisse un doute. */
          this.button.remove();
        }

        /* L'URL N'EST PLUS RÉÉCRITE VERS ?page=N. Elle l'était pour que
           le retour arrière retrouve la page atteinte — mais sur
           iPhone, quand Safari recharge l'onglet de lui-même (mémoire),
           ce rechargement partait de ?page=N et n'affichait QUE cette
           page : la visiteuse retombait sur une grille commençant au
           milieu des produits, sans comprendre où étaient passés les
           autres. Entre un retour arrière moins précis et une page qui
           semble amputée, le choix est vite fait : l'URL reste celle de
           la collection entière, et tout rechargement repart d'une
           grille complète depuis le début. */
      } catch {
        /* NE JAMAIS NAVIGUER SUR ÉCHEC. L'ancien repli faisait
           window.location.href = url : la moindre erreur — une
           limitation de débit, une réponse tronquée — rechargeait la
           page entière vers ?page=N. La visiteuse perdait sa position,
           les pages déjà chargées, et si la limitation persistait,
           tombait sur la page d'erreur de Shopify. Un repli censé
           sauver la situation la détruisait.

           Le bon repli est de rendre la main : le bouton se réactive,
           un nouveau clic réessaie, et la grille déjà affichée reste
           intacte. Le pire cas devient « le clic n'a rien fait », qui
           se corrige d'un clic — plus jamais « j'ai tout perdu ». */
        this.button.removeAttribute('aria-busy');
      } finally {
        window.avbApp?.spinner(false);
      }
    }

    /* ALLÈGE LES CARTES LOINTAINES. Ce ne sont pas les images qui
       écrasent Safari sur les longues grilles — la réserve les a déjà
       réduites à une par carte — c'est la machinerie : ~59 nœuds de
       pastilles Swatch King par carte (mesuré), un cœur de wishlist
       avec son Shadow DOM, un observateur d'intersection, une zone de
       défilement magnétique et un conteneur de requêtes CSS chacune.
       Vers 250 cartes, WebKit rend l'onglet.

       Au-delà des 104 cartes les plus récentes — deux pages, soit bien
       plus loin que ce que l'écran peut montrer — chaque carte est
       réduite à l'essentiel : son image, son lien, son titre et son
       prix. En remontant, on revoit donc les produits normalement ;
       seuls le carrousel au survol, le cœur et les pastilles manquent
       aux cartes anciennes, et un passage sur la fiche les retrouve.

       Le remplacement du carrousel par son propre lien débranche
       l'élément personnalisé, donc son observateur, par le mécanisme
       normal de déconnexion. */
    _alleger(target) {
      const cartes = target.querySelectorAll('.product-card');
      const garder = 104;

      for (let i = 0; i < cartes.length - garder; i++) {
        const carte = cartes[i];
        if (carte.hasAttribute('data-avb-allege')) continue;
        carte.setAttribute('data-avb-allege', '');

        const carrousel = carte.querySelector('avb-card-carousel');
        if (carrousel) {
          const lien = carrousel.querySelector('a.product-card__media');
          const image = lien && lien.querySelector('img');
          if (lien && image) {
            Array.from(lien.children).forEach((enfant) => {
              if (enfant !== image) enfant.remove();
            });
            carrousel.replaceWith(lien);
          }
        }

        carte
          .querySelectorAll(
            'variant-swatch-king, .swatches, .avb-card-swatches, ooo-wl-product-card-button, product-quick-buy, form[action*="/cart/add"], .product-card__quick-buy'
          )
          .forEach((noeud) => noeud.remove());
      }
    }

    /* Recompte les cartes réellement présentes plutôt que de déduire un
       total du numéro de page : une page incomplète, une carte retirée
       par un filtre, et le calcul théorique se décale. */
    _refreshProgress(target) {
      const total = Number(this.getAttribute('data-avb-total') || 0);
      const texte = this.querySelector('[data-avb-progress-text]');
      const barre = this.querySelector('[data-avb-progress-bar]');
      if (!total) return;

      const vus = Math.min(target.querySelectorAll('product-card').length, total);

      if (texte) {
        const gabarit = this.getAttribute('data-avb-progress-template');
        if (gabarit) {
          texte.textContent = gabarit.replace('@@S@@', String(vus)).replace('@@T@@', String(total));
        }
      }

      if (barre) {
        barre.style.setProperty('--avb-progress', `${Math.round((vus / total) * 100)}%`);
      }
    }
  }

  customElements.define('avb-load-more', AvbLoadMore);
}

/* Série C5 — recommandations fiche produit : fusion des deux rangées en
   une seule « Vu récemment ». Les deux sections (recently-viewed et
   related-products/« Pour vous ») se remplissent en asynchrone ; dès que
   chacune a des cartes, les cartes « Pour vous » sont ajoutées À LA SUITE
   des produits vus récemment, sans doublons (dédoublonnage par l'attribut
   handle des product-card), puis la section « Pour vous » est masquée.
   Nouveau visiteur sans historique : la liste « Vu récemment » ne se
   remplit jamais, la fusion n'a pas lieu et « Pour vous » reste visible
   (repli volontaire pour ne pas perdre les recommandations). */
(() => {
  const recent = document.querySelector('.shopify-section--recently-viewed-products');
  const forYou = document.querySelector('.shopify-section--related-products');
  if (!recent || !forYou) return;

  let merged = false;

  const tryMerge = () => {
    if (merged) return;

    /* Série D5 : en mode carrousel, le vrai slider est le
       <scroll-carousel> (l'élément <product-list> n'est qu'un wrapper
       des contrôles flottants) — cibler le wrapper ajoutait les cartes
       APRÈS le slider, en second bloc. On vise le scroll-carousel pour
       un seul slider continu. */
    const target = recent.querySelector('scroll-carousel') || recent.querySelector('product-list');
    const source = forYou.querySelector('scroll-carousel') || forYou.querySelector('product-list');
    if (!target || !source) return;

    const targetCards = target.querySelectorAll('product-card[handle]');
    const sourceCards = source.querySelectorAll('product-card[handle]');
    if (!targetCards.length || !sourceCards.length) return;

    merged = true;
    observer.disconnect();

    const seen = new Set(Array.from(targetCards, (card) => card.getAttribute('handle')));
    sourceCards.forEach((card) => {
      const handle = card.getAttribute('handle');
      if (handle && seen.has(handle)) return;
      if (handle) seen.add(handle);
      target.appendChild(card);
    });

    forYou.style.display = 'none';

    /* Les points de pagination générés côté serveur ne correspondent
       plus au nombre de cartes après fusion : masqués (le défilement
       tactile et les flèches flottantes restent fonctionnels). */
    recent.querySelectorAll('carousel-navigation').forEach((nav) => {
      nav.style.display = 'none';
    });
  };

  const observer = new MutationObserver(tryMerge);
  observer.observe(recent, { childList: true, subtree: true });
  observer.observe(forYou, { childList: true, subtree: true });
  tryMerge();
})();

/* Série D4 — liste de collections (avb-collection-nav) : au chargement
   d'une page collection, la ligne est défilée pour que la collection
   ACTIVE apparaisse au centre de la zone visible, sans animation
   (positionnement direct avant interaction). Calcul par delta de
   rectangles : indépendant du sens d'écriture (RTL) et sans effet sur
   le défilement vertical de la page, desktop et mobile. */
(() => {
  const active = document.querySelector('.avb-collection-nav__link.is-active');
  if (!active) return;

  const scroller = active.closest('.avb-collection-nav__list');
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

  const activeRect = active.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const delta = activeRect.left + activeRect.width / 2 - (scrollerRect.left + scroller.clientWidth / 2);
  scroller.scrollLeft += delta;
})();

/* Bouton « TOP » de retour en haut : fixe en bas à droite, il n'apparaît
   qu'à partir du moment où l'internaute a chargé une page supplémentaire
   de produits — c'est-à-dire après un clic sur « Charger + », donc au-delà
   des 52 premiers produits. Avant cela, la page reste assez courte pour
   qu'un raccourci n'ait pas d'intérêt.

   La détection ne dépend pas du clic lui-même : on compare le nombre de
   cartes présentes à celui du premier rendu. Cela couvre aussi bien le
   chargement sans rechargement de page que l'arrivée directe sur une
   page 2 via un lien. Remontée en douceur (instantanée si
   prefers-reduced-motion). */
if (!customElements.get('avb-back-to-top')) {
  class AvbBackToTop extends HTMLElement {
    connectedCallback() {
      this._visible = false;
      this._chargeSupplementaire = this._pageSuivante();
      this._descendu = false;

      this.querySelector('button')?.addEventListener('click', () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
      });

      /* CE QUI NE MARCHAIT PAS. La visibilité était calculée dans un
         écouteur `scroll` sur window, et la détection du chargement se
         faisait en comptant `.product-list product-card`. Deux
         dépendances fragiles pour une seule question — l'internaute
         est-elle descendue ? — et le bouton restait invisible en
         pratique.

         Un IntersectionObserver répond à la même question sans écouter
         le défilement : on place un repère à une hauteur d'écran du
         haut du document, et on regarde s'il est encore visible. Le
         navigateur fait le calcul, il n'y a plus d'écouteur à rater. */
      this._repere = document.createElement('div');
      this._repere.setAttribute('aria-hidden', 'true');
      this._repere.style.cssText =
        'position:absolute;top:100vh;inset-inline-start:0;width:1px;height:1px;pointer-events:none;visibility:hidden';
      document.body.appendChild(this._repere);

      this._observateur = new IntersectionObserver((entrees) => {
        const entree = entrees[0];
        /* Le repère sort par le HAUT quand on descend, et par le bas
           quand on est tout en haut de la page. Seul le premier cas
           nous intéresse. */
        this._descendu = !entree.isIntersecting && entree.boundingClientRect.top < 0;
        this._appliquer();
      });

      this._observateur.observe(this._repere);

      /* Le chargement d'une page supplémentaire est annoncé par
         avb-load-more. Plus besoin de compter les cartes. */
      document.addEventListener('avb:load-more', () => {
        this._chargeSupplementaire = true;
        this._appliquer();
      });

      this._appliquer();
    }

    disconnectedCallback() {
      this._observateur?.disconnect();
      this._repere?.remove();
    }

    /* Arriver directement sur une page 2 par un lien compte comme un
       chargement supplémentaire : l'internaute est déjà loin dans la
       liste, elle a autant besoin du raccourci. */
    _pageSuivante() {
      return Number(new URL(window.location.href).searchParams.get('page') || 1) > 1;
    }

    _appliquer() {
      const visible = this._chargeSupplementaire && this._descendu;
      if (visible === this._visible) return;
      this._visible = visible;
      this.toggleAttribute('hidden', !visible);
    }
  }

  customElements.define('avb-back-to-top', AvbBackToTop);
}

/* Série F1 — widget Trustpilot du tiroir panier : le tiroir est
   entièrement re-rendu (replaceChildren) à chaque changement de panier,
   et son contenu reste masqué tant qu'il n'a pas été ouvert une première
   fois. Dans les deux cas le widget réinséré n'a pas (ou plus) son
   iframe : on redemande son initialisation à Trustpilot, ce que le
   script bootstrap ne fait qu'au chargement de la page. Script absent ou
   bloqué : rien ne se passe, le lien texte de repli reste affiché. */
(() => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  let pending = false;

  const refresh = () => {
    pending = false;
    if (typeof window.Trustpilot?.loadFromElement !== 'function') return;

    drawer.querySelectorAll('.avb-trustpilot--cart .trustpilot-widget').forEach((widget) => {
      if (widget.querySelector('iframe')) return;
      window.Trustpilot.loadFromElement(widget, true);
    });
  };

  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(refresh);
  };

  new MutationObserver(schedule).observe(drawer, { childList: true });
  drawer.addEventListener('dialog:after-show', schedule);
})();

/* ------------------------------------------------------------------
   Accordéons exclusifs : ouvrir un volet referme celui qui était
   ouvert dans le même groupe (footer, fiche produit, FAQ…). Le thème
   laisse par défaut tous les volets ouverts simultanément, ce qui
   allonge la page et noie l'information.

   On ne touche pas au balisage : on écoute l'événement natif « toggle »
   des <details>, en phase de capture puisqu'il ne remonte pas. Le
   « groupe » est le conteneur d'accordéons le plus proche, sinon le
   parent direct — deux blocs distincts restent donc indépendants.
   ------------------------------------------------------------------ */
(() => {
  const GROUP_SELECTOR = '.accordion-group, .footer__accordion-group, .product-info__block-list, .accordion-list';

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      if (!details.classList.contains('accordion__disclosure')) return;

      const accordion = details.closest('.accordion');
      if (!accordion) return;

      const group = accordion.closest(GROUP_SELECTOR) || accordion.parentElement;
      if (!group) return;

      group.querySelectorAll('.accordion > .accordion__disclosure[open]').forEach((other) => {
        if (other === details) return;
        if ((other.closest(GROUP_SELECTOR) || other.closest('.accordion')?.parentElement) !== group) return;
        other.open = false;
        other.setAttribute('aria-expanded', 'false');
      });

      details.setAttribute('aria-expanded', 'true');
    },
    true
  );
})();

/* ------------------------------------------------------------------
   Vidéos de la section communauté : lecture en boucle sans son, et
   seulement quand la vignette est visible à l'écran — inutile de
   décoder une vidéo hors champ, surtout sur mobile où la section
   défile horizontalement.

   Le son est coupé par défaut : c'est la condition imposée par les
   navigateurs pour autoriser la lecture automatique, et c'est aussi ce
   qu'attend une internaute qui arrive sur une page. Le bouton rétablit
   le son à la demande ; activer le son sur une vidéo coupe les autres,
   pour ne jamais avoir deux bandes-son simultanées.
   ------------------------------------------------------------------ */
(() => {
  const videos = document.querySelectorAll('.avb-community__video');
  if (!videos.length) return;

  const play = (video) => {
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            play(video);
          } else {
            video.pause();
            // une vidéo qui sort de l'écran ne doit pas continuer à
            // jouer du son en arrière-plan
            video.muted = true;
            const button = video.closest('.avb-community__video-wrapper')?.querySelector('[data-avb-sound-toggle]');
            if (button) button.setAttribute('aria-pressed', 'false');
          }
        });
      },
      { threshold: 0.4 }
    );

    videos.forEach((video) => observer.observe(video));
  } else {
    videos.forEach(play);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-avb-sound-toggle]');
    if (!button) return;

    const wrapper = button.closest('.avb-community__video-wrapper');
    const video = wrapper?.querySelector('.avb-community__video');
    if (!video) return;

    const unmute = video.muted;

    if (unmute) {
      document.querySelectorAll('.avb-community__video').forEach((other) => {
        if (other === video) return;
        other.muted = true;
      });
      document.querySelectorAll('[data-avb-sound-toggle]').forEach((other) => {
        if (other !== button) other.setAttribute('aria-pressed', 'false');
      });
    }

    video.muted = !unmute;
    button.setAttribute('aria-pressed', unmute ? 'true' : 'false');
    if (unmute) play(video);
  });
})();

/* ------------------------------------------------------------------
   Vidéo de matière : le son se met à l'ouverture du volet.

   POURQUOI PAS DÈS LE CHARGEMENT. Aucun navigateur n'autorise une
   lecture automatique avec du son : la vidéo resterait figée sur sa
   première image. La règle ne se contourne pas, elle se respecte — il
   faut un geste de la visiteuse.

   Or ce geste existe déjà : le volet « La matière » est replié, et il
   faut le déplier pour voir la vidéo. Ce clic vaut autorisation. On
   écoute donc l'ouverture du <details> et on lance la lecture avec le
   son dans la foulée, tant que l'autorisation du navigateur est encore
   valable (quelques secondes après le geste).

   À la fermeture du volet, la vidéo se met en pause et retrouve sa
   sourdine : une bande-son qui continue sur une page qu'on a repliée
   serait incompréhensible. Le bouton de son reste disponible pour
   couper ou rétablir à la main, il partage le module de la Communauté.

   ROND DE CHARGEMENT. Le média est en `preload="none"` : entre le clic
   et la première image, la place est vide. La classe `is-prete`, posée
   dès que la vidéo a de quoi s'afficher, retire le rond décrit dans
   custom.css.
   ------------------------------------------------------------------ */
(() => {
  const wrappers = document.querySelectorAll('[data-avb-matiere-video]');
  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    const video = wrapper.querySelector('video');
    if (!video) return;

    const bouton = wrapper.querySelector('[data-avb-sound-toggle]');
    const volet = wrapper.closest('details');

    /* `loadeddata` suffit : la première image est décodée, il y a donc
       quelque chose à regarder. On garde `playing` en filet au cas où
       l'événement serait passé avant l'écoute. */
    const prete = () => wrapper.classList.add('is-prete');
    if (video.readyState >= 2) prete();
    video.addEventListener('loadeddata', prete, { once: true });
    video.addEventListener('playing', prete);

    if (!volet) return;

    volet.addEventListener('toggle', () => {
      if (volet.open) {
        /* Les autres vidéos de la page se taisent : jamais deux
           bandes-son en même temps. */
        document.querySelectorAll('.avb-community__video').forEach((autre) => {
          if (autre !== video) autre.muted = true;
        });
        document.querySelectorAll('[data-avb-sound-toggle]').forEach((autre) => {
          if (autre !== bouton) autre.setAttribute('aria-pressed', 'false');
        });

        video.muted = false;
        if (bouton) bouton.setAttribute('aria-pressed', 'true');

        const lecture = video.play();
        if (lecture && typeof lecture.catch === 'function') {
          /* Si le navigateur refuse malgré tout le son, on retombe sur
             une lecture muette plutôt que sur une vidéo figée. */
          lecture.catch(() => {
            video.muted = true;
            if (bouton) bouton.setAttribute('aria-pressed', 'false');
            const repli = video.play();
            if (repli && typeof repli.catch === 'function') repli.catch(() => {});
          });
        }
      } else {
        video.pause();
        video.muted = true;
        if (bouton) bouton.setAttribute('aria-pressed', 'false');
      }
    });
  });
})();

/* ------------------------------------------------------------------
   Fiche produit : le bouton « Add to wishlist » de l'application est
   déplacé au niveau du titre du produit, sous forme de cœur seul. Le
   long bouton pleine largeur sous le prix ajoutait une ligne sans
   valeur ; à côté du titre, le geste reste disponible sans peser sur la
   colonne d'achat.

   L'élément est injecté par l'application après le rendu : on l'attend
   avec un observateur, puis on cesse d'écouter dès qu'il est placé.
   ------------------------------------------------------------------ */
(() => {
  const info = document.querySelector('.product-info');
  if (!info) return;

  const place = () => {
    const button = info.querySelector('ooo-wl-access-button, .ooo-wl-access-button');
    if (!button) return false;

    const title = info.querySelector('.product-title');
    if (!title) return false;

    if (button.closest('.avb-product-title-row')) return true;

    const row = document.createElement('div');
    row.className = 'avb-product-title-row';
    title.parentNode.insertBefore(row, title);
    row.appendChild(title);
    row.appendChild(button);
    button.classList.add('avb-product-wishlist');
    return true;
  };

  if (place()) return;

  const observer = new MutationObserver(() => {
    if (place()) observer.disconnect();
  });

  observer.observe(info, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 10000);
})();

/* ------------------------------------------------------------------
   Menu de langue mobile : fermeture au clic à l'extérieur et à la
   touche Échap.

   En repositionnant ce menu en liste déroulante, on a neutralisé la
   partie interne qui couvrait l'écran — or c'est elle qui recevait les
   clics extérieurs et déclenchait la fermeture. On rétablit donc ce
   comportement explicitement, en appelant la méthode hide() du
   composant du thème.
   ------------------------------------------------------------------ */
(() => {
  // À l'ouverture, le thème déplace le menu dans le <body> : on le
  // retrouve donc par son identifiant plutôt que par son conteneur.
  const openPopovers = () =>
    document.querySelectorAll('x-popover[id*="avb-header-language"][open]');

  const close = () => {
    openPopovers().forEach((popover) => {
      if (typeof popover.hide === 'function') popover.hide();
    });
  };

  document.addEventListener('click', (event) => {
    // Clic sur le sélecteur lui-même ou dans le menu ouvert : on laisse
    // le composant du thème gérer.
    if (event.target.closest?.('.avb-header-lang')) return;
    if (event.target.closest?.('x-popover[id*="avb-header-language"]')) return;
    close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
})();

/* ------------------------------------------------------------------
   Menu de langue : comportement de liste déroulante sur mobile aussi.

   Le composant du thème applique trois traitements sous 1000 px, tous
   pensés pour un panneau plein écran :
     1. il DÉPLACE le menu dans <body> (shouldAppendToBody) ;
     2. il VERROUILLE le défilement de la page (shouldLock) ;
     3. il ANIME le contenu depuis le bas de l'écran, avec un voile.

   C'est ce troisième point qui provoquait l'apparition fugace du
   panneau du bas à l'ouverture ET à la fermeture : le CSS ne peut rien
   contre une animation posée en style inline par le script.

   On surcharge donc ces trois comportements sur cette seule instance,
   en gardant l'interface attendue par le thème — la méthode
   d'animation doit renvoyer un objet exposant « finished » et
   « finish », ce que fournit nativement l'API Web Animations.
   ------------------------------------------------------------------ */
(() => {
  const SELECTOR = 'x-popover[id*="avb-header-language"]';

  const patch = (popover) => {
    if (!popover || popover.dataset.avbPatched === 'true') return;
    popover.dataset.avbPatched = 'true';

    Object.defineProperty(popover, 'shouldAppendToBody', { get: () => false });
    Object.defineProperty(popover, 'shouldLock', { get: () => false });

    popover.createEnterAnimationControls = function () {
      return this.animate({ opacity: [0, 1] }, { duration: 160, fill: 'none' });
    };

    popover.createLeaveAnimationControls = function () {
      return this.animate({ opacity: [1, 0] }, { duration: 140, fill: 'none' });
    };
  };

  const patchAll = () => document.querySelectorAll(SELECTOR).forEach(patch);

  // Les éléments personnalisés peuvent ne pas encore être définis au
  // moment où ce script s'exécute : on attend leur enregistrement.
  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('x-popover').then(patchAll);
  }

  patchAll();
  document.addEventListener('DOMContentLoaded', patchAll);
})();

/* Page relais TikTok (sections/avb-landing-tiktok.liquid).

   Le bouton porte sa destination par défaut ; ce script la remplace par
   la collection déduite du paramètre « collection » de l'adresse, et
   surtout RECOPIE les paramètres de campagne vers la destination —
   ttclid, utm_*, gclid. Sans cette recopie, TikTok perd la trace de la
   visite dès le relais et les ventes ne sont plus attribuées.

   Sans JavaScript, le bouton conserve son lien par défaut : la visiteuse
   arrive sur une page cohérente plutôt que sur une erreur. */
(() => {
  const button = document.querySelector('[data-avb-landing]');
  if (!button) return;

  const params = new URLSearchParams(window.location.search);
  let key = (params.get('collection') || '').trim();

  /* La clé ne sert qu'à construire une adresse : lettres, chiffres et
     tirets uniquement. Une valeur fabriquée à la main dans la barre
     d'adresse ne peut donc pas injecter autre chose. */
  if (!/^[a-z0-9-]+$/i.test(key)) key = '';

  let map = {};
  try {
    map = JSON.parse(button.getAttribute('data-avb-map') || '{}') || {};
  } catch {}

  let path = button.getAttribute('data-avb-default');
  if (key) {
    path = map[key] || '/collections/' + button.getAttribute('data-avb-pattern') + key;
  }

  params.delete('collection');
  params.set('ref', 'tiktok');

  const url = button.getAttribute('data-avb-prefix') + path + '?' + params.toString();
  button.setAttribute('href', url);

  /* Préchargement : la collection est téléchargée pendant que la
     visiteuse regarde cet écran, l'appui n'attend plus le réseau. */
  const hint = document.createElement('link');
  hint.rel = 'prefetch';
  hint.href = url;
  document.head.appendChild(hint);
})();

/* Aperçu agrandi de la section communauté.

   Le visuel d'origine n'est pas dupliqué : il est DÉPLACÉ dans le
   dialogue, puis remis exactement à sa place à la fermeture. Une copie
   aurait relancé le téléchargement de la vidéo et perdu sa position de
   lecture ; ici la lecture se poursuit, simplement en plus grand.

   L'aperçu est volontairement borné (voir custom.css) : il n'occupe pas
   tout l'écran et laisse voir la page autour, sans voile opaque. Le
   contexte reste visible, la fermeture reste évidente.

   Le <dialog> natif apporte la touche Échap, le piège de focus et le
   retour du focus au bouton d'origine — trois comportements qu'il aurait
   fallu réécrire à la main. */
(() => {
  const dialog = document.querySelector('[data-avb-preview]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const stage = dialog.querySelector('[data-avb-preview-stage]');
  const closeButton = dialog.querySelector('[data-avb-preview-close]');
  const boite = stage?.closest('.avb-preview__box') || stage;

  let origin = null; /* emplacement d'où le visuel a été retiré */
  let moved = null;

  /* LE CADRE PREND LES PROPORTIONS DU VISUEL.

     Le cadre avait une hauteur fixe et le média s'y inscrivait en
     `contain` : une vidéo au format paysage laissait deux bandes vides
     en haut et en bas, transparentes, avec un bouton flottant à chaque
     extrémité — on ne savait plus où commençait l'image.

     On donne donc au cadre le rapport largeur/hauteur du média dès qu'il
     est connu. Le cadre épouse alors exactement le visuel, quelle que
     soit l'orientation, et les boutons se posent sur ses bords réels. */
  const proportionner = (media) => {
    const image = media.matches('img') ? media : media.querySelector('img');
    const video = media.matches('video') ? media : media.querySelector('video');

    const appliquer = (l, h) => {
      if (l > 0 && h > 0) boite.style.aspectRatio = `${l} / ${h}`;
    };

    if (video) {
      if (video.videoWidth) appliquer(video.videoWidth, video.videoHeight);
      else video.addEventListener('loadedmetadata', () => appliquer(video.videoWidth, video.videoHeight), { once: true });
      return;
    }

    if (image) {
      if (image.naturalWidth) appliquer(image.naturalWidth, image.naturalHeight);
      else image.addEventListener('load', () => appliquer(image.naturalWidth, image.naturalHeight), { once: true });
    }
  };

  /* PASSER AU MÉDIA VOISIN SANS REFERMER.

     Ouvert en grand, un média de la Communauté n'était qu'une impasse :
     pour voir le suivant, il fallait refermer, retrouver la rangée,
     viser la vignette d'à côté. Le geste attendu est celui de toutes
     les galeries — on glisse, ou on tape une flèche.

     La rangée est celle du média ouvert : tous les visuels du même
     défilé, dans l'ordre d'affichage. Un croquis de mesures ou une
     vidéo de matière n'appartient à aucun défilé : pas de rangée, pas
     de flèches. */
  const navButtons = Array.from(dialog.querySelectorAll('[data-avb-preview-nav]'));
  let rangee = [];
  let position = -1;

  const releverRangee = (media) => {
    const defile = media.closest('.avb-community__scroller');
    rangee = defile ? Array.from(defile.querySelectorAll(SELECTEUR_MEDIA)) : [];
    position = rangee.indexOf(media);

    const seul = rangee.length < 2;
    navButtons.forEach((bouton) => { bouton.hidden = seul; });
  };

  const open = (media) => {
    const holder = media.parentElement;
    if (!holder) return;

    origin = { parent: holder, next: media.nextSibling };
    moved = media;
    stage.append(media);
    if (boite) proportionner(media);
    releverRangee(media);
    if (!dialog.open) dialog.showModal();

    /* Le dialogue natif donne le focus à la croix en s'ouvrant, et
       Safari iOS dessine alors l'anneau comme après un Tab. On le
       retire aussitôt : au doigt, personne n'a demandé ce focus. Au
       clavier, Tab le redonnera. */
    closeButton?.blur();

    const video = media.matches('video') ? media : media.querySelector('video');
    if (video) video.play().catch(() => {});
  };

  const restore = () => {
    if (boite) boite.style.aspectRatio = '';
    if (!moved || !origin) return;

    /* Une vidéo remise à sa place ne doit pas continuer à jouer avec
       le son : la carte la reprend muette, comme avant l'agrandissement. */
    const video = moved.matches('video') ? moved : moved.querySelector('video');
    if (video) { video.pause(); video.muted = true; }
    moved.querySelector('[data-avb-sound-toggle]')?.setAttribute('aria-pressed', 'false');

    origin.parent.insertBefore(moved, origin.next);
    moved = null;
    origin = null;
  };

  const naviguer = (sens) => {
    if (rangee.length < 2 || position < 0) return;
    const cible = rangee[(position + sens + rangee.length) % rangee.length];
    if (!cible || cible === moved) return;
    restore();
    open(cible);
  };

  navButtons.forEach((bouton) => {
    bouton.addEventListener('click', () => naviguer(Number(bouton.dataset.avbPreviewNav) || 1));
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') naviguer(1);
    else if (event.key === 'ArrowLeft') naviguer(-1);
  });

  /* Glissement tactile : un déplacement horizontal net, plus large
     que haut, passe au voisin. Le seuil écarte les tremblements d'un
     simple tap, qui lui continue de refermer. */
  let toucheX = 0;
  let toucheY = 0;

  dialog.addEventListener('touchstart', (event) => {
    const touche = event.changedTouches[0];
    toucheX = touche.clientX;
    toucheY = touche.clientY;
  }, { passive: true });

  dialog.addEventListener('touchend', (event) => {
    const touche = event.changedTouches[0];
    const dx = touche.clientX - toucheX;
    const dy = touche.clientY - toucheY;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    naviguer(dx < 0 ? 1 : -1);
  }, { passive: true });

  /* UNE IMAGE N'A PAS BESOIN DU MÊME AGRANDISSEMENT QU'UNE VIDÉO.

     Une vidéo agrandie se regarde : la montrer en grand suffit, et le
     dialogue ci-dessus fait exactement cela sans rien charger de plus.

     Une image cotée, elle, se lit. L'afficher en grand ne sert à rien
     si l'on ne peut pas s'en approcher : sur le croquis de mesures, les
     chiffres restent minuscules quel que soit le cadre. Il faut le
     zoom, le pincement, le double-tap.

     Or le thème embarque déjà tout cela pour la galerie produit — c'est
     PhotoSwipe, et la visionneuse est déjà téléchargée. On la réutilise
     plutôt que d'en réécrire une : le geste est le même partout sur la
     fiche, avec le même plafond de trois fois. Le module n'est chargé
     qu'au premier clic, pour ne pas peser sur l'affichage initial. */
  let visionneuse = null;

  const ouvrirImage = async (image) => {
    try {
      if (!visionneuse) {
        const { PhotoSwipeLightbox } = await import('vendor');
        const textes = window.themeVariables?.strings || {};

        visionneuse = new PhotoSwipeLightbox({
          pswpModule: () => import('photoswipe'),
          bgOpacity: 1,
          maxZoomLevel: 3,
          closeTitle: textes.closeGallery,
          zoomTitle: textes.zoomGallery,
          errorMsg: textes.errorGallery,
          /* Une classe propre sur la racine : elle permet de replacer
             la croix sans toucher à la galerie produit, qui partage la
             même visionneuse et garde ses propres repères. */
          mainClass: 'avb-pswp',
          /* Une seule image : les flèches et le compteur n'auraient
             rien à annoncer. */
          arrowPrev: false,
          arrowNext: false,
          counter: false,
          zoom: false,
          /* LA CROIX D'ORIGINE EST DÉSACTIVÉE, ET REMPLACÉE PLUS BAS.

             Celle de PhotoSwipe n'aurait pas fonctionné : la barre
             d'outils du thème est déclarée `pointer-events: none`, et
             seuls ses propres boutons ronds récupèrent le clic. La
             croix native s'affichait donc sans être cliquable — on ne
             pouvait refermer qu'en tapant à côté. */
          close: false
        });

        visionneuse.on('uiRegister', () => {
          visionneuse.pswp.ui.registerElement({
            name: 'avb-fermer',
            order: 9,
            isButton: true,
            className: 'circle-button circle-button--lg avb-pswp__fermer',
            html: '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false"><path d="M2.5 2.5l11 11M13.5 2.5l-11 11" stroke="currentColor" stroke-width="1.3"/></svg>',
            onClick: () => visionneuse.pswp.close()
          });
        });

        visionneuse.init();
      }

      /* Les dimensions déclarées décrivent le fichier d'origine ; c'est
         d'elles que PhotoSwipe déduit jusqu'où il peut agrandir sans
         flou. À défaut, on se rabat sur celles de la variante chargée. */
      const largeur = parseInt(image.getAttribute('width'), 10) || image.naturalWidth;
      const hauteur = parseInt(image.getAttribute('height'), 10) || image.naturalHeight;

      visionneuse.loadAndOpen(0, [
        {
          thumbnailElement: image,
          src: image.currentSrc || image.src,
          srcset: image.getAttribute('srcset') || undefined,
          msrc: image.currentSrc || image.src,
          width: largeur,
          height: hauteur,
          alt: image.alt
        }
      ]);

      return true;
    } catch (erreur) {
      /* Si la visionneuse ne se charge pas — réseau coupé, module
         absent — on ne laisse pas la loupe sans effet : le dialogue
         d'aperçu prend le relais. Agrandir sans zoomer vaut mieux que
         ne rien faire. */
      return false;
    }
  };

  /* Deux familles de médias agrandissables.

     La Communauté a son propre conteneur, avec une vidéo ou une photo.
     Le croquis de mesures et le visuel de matière sont dans un volet
     dépliant. On cherche le conteneur le plus proche parmi les formes
     connues, puis le premier média qu'il contient, et on l'oriente
     selon sa nature. */
  const SELECTEUR_MEDIA =
    '.avb-community__video-wrapper, .avb-community__image, .avb-dimensions__image, .avb-matiere__image';

  const agrandir = async (media) => {
    if (media.matches('img')) {
      const fait = await ouvrirImage(media);
      if (fait) return;
    }
    open(media);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-avb-zoom]');
    if (!trigger) return;

    const conteneur = trigger.closest('.avb-community__media, [data-avb-zoom-media]');
    const media = conteneur?.querySelector(SELECTEUR_MEDIA);
    if (media) agrandir(media);
  });

  /* La loupe est posée sur le média : le média lui-même doit ouvrir
     l'aperçu, sans quoi on demande de viser une pastille de deux
     centimètres quand la cible évidente occupe tout l'espace. On ne
     déclenche que là où une loupe existe — ailleurs, une image n'est
     pas censée s'agrandir.

     Le bouton de son est posé SUR le média, donc dans la zone qui
     agrandit : le couper ouvrait l'aperçu par-dessus. Il est exclu, au
     même titre que la loupe. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-avb-zoom]')) return;
    if (event.target.closest('[data-avb-sound-toggle]')) return;

    const conteneur = event.target.closest('[data-avb-zoom-media]');
    if (!conteneur || !conteneur.querySelector('[data-avb-zoom]')) return;

    const media = conteneur.querySelector(SELECTEUR_MEDIA);
    if (media) agrandir(media);
  });

  closeButton?.addEventListener('click', () => dialog.close());

  /* UN CLIC N'IMPORTE OÙ REFERME.

     La règle précédente ne fermait que si le clic tombait sur le
     dialogue lui-même. Depuis que le cadre occupe la quasi-totalité de
     l'écran pour agrandir le visuel, il ne reste presque plus de
     dialogue à viser : le clic tombait sur le cadre ou sur les marges
     transparentes du média — qui lui appartiennent, `object-fit`
     n'enlevant rien à la surface de l'élément — et il ne se passait
     rien. Il fallait viser la croix.

     On ferme donc au moindre clic dans la couche, y compris sur le
     visuel : c'est le geste attendu d'une vue agrandie, et il n'y a
     rien à manipuler dessus. Seul le bouton de son est épargné, sans
     quoi couper le son refermerait l'aperçu. */
  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-avb-sound-toggle]')) return;
    if (event.target.closest('[data-avb-preview-nav]')) return;
    dialog.close();
  });

  dialog.addEventListener('close', restore);
})();

/* Communauté : ouverture de la liste des produits tagués.

   POURQUOI PAS UN <details>. La liste doit apparaître sous le pseudo,
   donc le <details> aurait dû envelopper la photo, le pseudo et la
   liste — un <summary> ne pouvant pas être séparé de son parent. Or un
   <details> fermé masque tous ses enfants sauf le <summary> : la photo
   et le pseudo disparaissaient tant qu'on n'avait pas cliqué. D'où ce
   bouton, qui ne pilote que ce qu'on lui demande.

   Une seule liste ouverte à la fois : deux cartes ouvertes côte à côte
   dans un défilé horizontal donnent une rangée en escalier, illisible. */
(() => {
  document.addEventListener('click', (event) => {
    const bouton = event.target.closest('[data-avb-shop-toggle]');
    if (!bouton) return;

    const carte = bouton.closest('.avb-community__item');
    const liste = carte?.querySelector('[data-avb-shop-panel]');
    if (!liste) return;

    const ouvrir = liste.hidden;

    /* On referme les autres avant d'ouvrir celle-ci. */
    document.querySelectorAll('[data-avb-shop-panel]').forEach((autre) => {
      if (autre === liste) return;
      autre.hidden = true;
      autre
        .closest('.avb-community__item')
        ?.querySelector('[data-avb-shop-toggle]')
        ?.setAttribute('aria-expanded', 'false');
    });

    liste.hidden = !ouvrir;
    bouton.setAttribute('aria-expanded', String(ouvrir));
  });
})();

/* Blocage du zoom par pincement sur mobile.

   CE QUI S'EST PASSÉ, ET POURQUOI CE BLOC A MAIGRI.

   Une première version interceptait aussi le touchmove à plusieurs
   doigts et la double tape. Résultat observé sur un vrai iPhone : la
   page se retrouvait zoomée sans qu'on l'ait demandé, et il devenait
   impossible d'en sortir.

   La cause n'était pas le pincement. iOS zoome AUTOMATIQUEMENT la page
   dès qu'on touche un champ de formulaire dont le texte fait moins de
   16 px — et le thème en comptait 458. Bloquer le pincement n'empêchait
   pas ce zoom : cela supprimait seulement le moyen d'en revenir.

   La vraie correction est donc dans custom.css, où les champs passent à
   16 px sur mobile. Ici on se contente d'intercepter les événements
   gesture de Safari, qui sont le seul mécanisme que touch-action ne
   couvre pas. On ne touche plus ni au touchmove ni à la double tape :
   ils ne bloquaient rien d'utile et fermaient la porte de sortie.

   Écouteurs passive: false, sans quoi preventDefault serait ignoré. */
(() => {
  const bloquer = (event) => event.preventDefault();

  ['gesturestart', 'gesturechange', 'gestureend'].forEach((nom) => {
    document.addEventListener(nom, bloquer, { passive: false });
  });
})();

/* Barre d'achat persistante sur la fiche produit (mobile).

   Elle n'apparaît qu'une fois le bouton d'origine sorti de l'écran :
   avant cela elle ferait doublon et masquerait de la photo pour rien.
   La détection passe par IntersectionObserver, pas par un calcul de
   position au défilement — aucun travail à chaque pixel parcouru.

   ELLE NE DUPLIQUE PAS LA LOGIQUE D'ACHAT. Le bouton commande à distance
   le vrai bouton du thème, celui qui connaît la variante choisie, les
   options et le formulaire. Si le thème évolue, la barre suit.

   Le prix est resynchronisé quand le thème remplace le bloc de prix au
   changement de variante. */
(() => {
  const barre = document.querySelector('[data-avb-buybar]');
  if (!barre) return;

  const boutonOrigine = document.querySelector('buy-buttons button[type="submit"], .buy-buttons button[type="submit"]');
  if (!boutonOrigine) return;

  barre.hidden = false;

  /* Le seuil à 0 suffit : on veut savoir si le bouton touche encore
     l'écran, pas dans quelle proportion. */
  const veille = new IntersectionObserver(
    ([entree]) => {
      barre.classList.toggle('is-visible', !entree.isIntersecting);
    },
    { threshold: 0 }
  );

  veille.observe(boutonOrigine);

  barre.querySelector('[data-avb-buybar-add]')?.addEventListener('click', () => {
    boutonOrigine.click();
  });

  /* Suivi du prix. Le thème réécrit le bloc de prix à chaque changement
     de variante ; on recopie ses valeurs plutôt que de recalculer.

     DEUX PIÈGES ÉVITÉS ICI.

     Les libellés cachés : le bloc contient des <span class="sr-only">
     destinés aux lecteurs d'écran — « Prix soldé », « Prix habituel ».
     Invisibles à l'œil, bien présents dans le texte : une simple lecture
     de textContent donnait « Sale price39,99 € ».

     Les promotions : le thème rend deux éléments distincts, <sale-price>
     pour le prix payé et <compare-at-price> pour le prix barré, ce
     dernier n'existant que si la variante est soldée. On les lit
     séparément plutôt que de découper une chaîne, et le prix barré
     disparaît de lui-même sur une variante au prix plein. */
  const cible = barre.querySelector('[data-avb-buybar-price]');
  const cibleBarre = barre.querySelector('[data-avb-buybar-compare]');
  const source = document.querySelector('.product-info .price-list');

  if (cible && source) {
    const lire = (element) => {
      if (!element) return '';
      const copie = element.cloneNode(true);
      copie.querySelectorAll('.sr-only, .visually-hidden').forEach((n) => n.remove());
      return copie.textContent.trim().replace(/\s+/g, ' ');
    };

    const copier = () => {
      const paye = lire(source.querySelector('sale-price'));
      if (paye) cible.textContent = paye;

      if (!cibleBarre) return;

      const barreTexte = lire(source.querySelector('compare-at-price'));

      if (barreTexte) {
        /* On conserve le libellé pour lecteurs d'écran déjà rendu par
           Liquid, et on ne remplace que le montant qui le suit. */
        const libelle = cibleBarre.querySelector('.sr-only');
        cibleBarre.textContent = '';
        if (libelle) cibleBarre.appendChild(libelle);
        cibleBarre.append(barreTexte);
        cibleBarre.hidden = false;
      } else {
        cibleBarre.hidden = true;
      }
    };

    new MutationObserver(copier).observe(source, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    copier();
  }
})();

/* ======================================================================
   Favoris : compteur dans l'en-tête et état vide de la page

   L'API JavaScript de Wishlist Power est réservée au forfait Pro. Sur
   les autres, `window.Maestrooo.wishlist` n'est pas un objet mais une
   phrase : « You don't have access to the wishlist API. » Le compteur
   ne pouvait donc rien lire au chargement d'une page, et disparaissait
   à chaque navigation alors que l'application, elle, se souvenait
   parfaitement des favoris.

   TROIS SOURCES, PAR ORDRE DE FIABILITÉ.

   1. La page Favoris elle-même. L'application y rend ses cartes dans un
      shadow DOM ouvert : on peut les compter. C'est la seule source
      vraie, et chaque visite sur cette page resynchronise le reste.
   2. Les événements d'ajout et de retrait, qui portent le nouveau
      total. Ils fonctionnent sur tous les forfaits.
   3. Le dernier total connu, conservé dans le navigateur. C'est lui qui
      permet d'afficher le compteur au chargement d'une page.

   CE QUE LA TROISIÈME NE GARANTIT PAS. Un favori ajouté depuis un autre
   appareil ne sera pas compté ici tant que rien ne se passe dans ce
   navigateur. Le chiffre est alors en retard, jamais faux dans l'autre
   sens — et la première visite sur la page Favoris le corrige.

   L'ÉTAT VIDE NE SE FIE QU'À LA SOURCE VRAIE. Afficher « votre liste
   est vide » sur la foi d'un total mis en cache serait le meilleur
   moyen de l'afficher au-dessus d'une liste pleine.
   ====================================================================== */
(() => {
  const compteurs = document.querySelectorAll('[data-avb-wishlist-count]');
  const videBloc = document.querySelector('[data-avb-wishlist-empty]');
  const CLE = 'avb-wishlist-count';

  if (!compteurs.length && !videBloc) return;

  const memoriser = (nombre) => {
    try {
      localStorage.setItem(CLE, String(nombre));
    } catch {
      /* Navigation privée, quota plein : le compteur se contentera des
         événements de la session en cours. */
    }
  };

  const relire = () => {
    try {
      const brut = localStorage.getItem(CLE);
      if (brut === null) return null;
      const nombre = Number(brut);
      return Number.isFinite(nombre) && nombre >= 0 ? nombre : null;
    } catch {
      return null;
    }
  };

  const peindre = (nombre, fiable) => {
    compteurs.forEach((element) => {
      if (nombre > 0) {
        /* Au-delà de 9, la pastille deviendrait plus large que le cœur
           qu'elle décore. On plafonne l'affichage, pas la valeur. */
        element.textContent = nombre > 9 ? '9+' : String(nombre);
        element.hidden = false;

        /* Le gabarit vient du fichier de langue, posé sur l'élément par
           Liquid : le script n'écrit jamais de texte en dur. */
        const gabarit = element.dataset.avbWishlistLabel || '';
        if (gabarit) element.setAttribute('title', gabarit.replace('{{ count }}', String(nombre)));
      } else {
        element.textContent = '';
        element.hidden = true;
      }
    });

    if (videBloc && fiable) videBloc.hidden = nombre !== 0;
  };

  /* ── 3. Dernier total connu, pour que le compteur existe dès l'arrivée
     sur la page. ─────────────────────────────────────────────────── */
  const memoire = relire();
  if (memoire !== null) peindre(memoire, false);

  /* ── 1. Source vraie : les cartes rendues par l'application sur la
     page Favoris. Le shadow root est ouvert, donc lisible. ────────── */
  const conteneurPage = document.querySelector('ooo-wl-page-container');

  if (conteneurPage) {
    let dernier = null;

    const compterCartes = () => {
      const racine = conteneurPage.shadowRoot;
      if (!racine) return;

      /* Le conteneur de cartes n'existe qu'une fois la liste chargée.
         Tant qu'il est absent, on ne conclut rien : zéro carte et
         « pas encore rendu » se ressemblent trop. */
      const grille = racine.getElementById('ooo-wl-page-product-card-container');
      if (!grille) return;

      const total = grille.querySelectorAll('ooo-wl-page-product-card').length;
      if (total === dernier) return;

      dernier = total;
      memoriser(total);
      peindre(total, true);
    };

    new MutationObserver(compterCartes).observe(conteneurPage, {
      childList: true,
      subtree: true,
    });

    /* Le shadow root peut être attaché après nous : on repasse
       brièvement plutôt que de dépendre d'un événement dont on ne
       maîtrise pas le nom. */
    let essais = 0;
    const minuteur = setInterval(() => {
      essais += 1;
      compterCartes();
      if (dernier !== null || essais > 30) clearInterval(minuteur);
    }, 300);

    compterCartes();
  }

  /* ── 2. Événements d'ajout et de retrait. Ils portent déjà le nouveau
     total : aucun appel réseau n'est nécessaire. ──────────────────── */
  ['ooo-wishlist:product-added', 'ooo-wishlist:product-removed'].forEach((nom) => {
    document.addEventListener(nom, (evenement) => {
      const detail = evenement.detail || {};
      let total = null;

      if (typeof detail.wishlist_products_count === 'number') {
        total = detail.wishlist_products_count;
      } else if (Array.isArray(detail.wishlist_product_list)) {
        total = detail.wishlist_product_list.length;
      }

      if (total === null) return;

      memoriser(total);
      peindre(total, true);
    });
  });
})();

/* ======================================================================
   Header transparent : rester transparent tant que le visuel est là

   CE QUI SE PASSAIT. Le thème ne surveille pas le défilement pour
   décider quand le header redevient opaque : il pose un repère
   invisible d'un pixel près du haut du document et regarde s'il est
   encore visible. Ce repère est à 10 px du haut, ou à la hauteur de la
   barre d'annonce quand il y en a une. Autrement dit, une quarantaine
   de pixels de défilement suffisaient à rendre le header blanc — alors
   que le diaporama occupait encore tout l'écran.

   Le mécanisme est bon, c'est sa position qui était mauvaise. On
   déplace le repère au bas de la première section, moins la hauteur du
   header : le header devient opaque au moment précis où il quitte le
   visuel, ce qui est le seul moment où ça a un sens.

   On ne touche pas à l'observateur du thème, on lui donne simplement un
   meilleur repère. Rien à désactiver, rien à dupliquer.
   ====================================================================== */
(() => {
  const repere = document.getElementById('header-scroll-tracker');
  const premiere = document.querySelector('.shopify-section:first-child');

  /* Sans section transparente en tête de page, le repère du thème est
     déjà au bon endroit : on ne fait rien. */
  if (!repere || !premiere || !premiere.querySelector('[allow-transparent-header]')) return;

  const entete = document.querySelector('.shopify-section--header .header, .header');

  const placer = () => {
    const bas = premiere.getBoundingClientRect().bottom + window.scrollY;
    const hauteurEntete = entete ? entete.getBoundingClientRect().height : 0;

    /* Le plancher de 10 px reprend la valeur d'origine du thème : si la
       première section était plus courte que le header, un repère
       négatif ne serait jamais franchi et le header resterait
       transparent au-dessus du contenu. */
    repere.style.top = `${Math.max(10, Math.round(bas - hauteurEntete))}px`;
  };

  placer();

  /* Le diaporama grandit quand ses images arrivent : mesurer une seule
     fois au chargement figerait une hauteur provisoire. */
  if ('ResizeObserver' in window) {
    new ResizeObserver(placer).observe(premiere);
  }

  window.addEventListener('resize', placer, { passive: true });
  window.addEventListener('load', placer, { once: true });
})();

/* ------------------------------------------------------------------
   Barre de livraison offerte : seuil converti, panier suivi en direct

   TROIS PROBLÈMES, UN SEUL ENDROIT POUR LES RÉSOUDRE.

   1. LA DEVISE. Le seuil est saisi une fois dans la devise de la
      boutique. Un nombre nu conviendrait entre euro et dollar, dont les
      ordres de grandeur se ressemblent ; il devient faux en dirhams ou
      en pesos — 250 MAD valent environ 25 €, la livraison serait
      offerte sur presque tout. Shopify publie le taux de change dans
      Shopify.currency.rate : c'est la seule source disponible côté
      client, et elle est déjà celle qu'utilise le composant du thème
      pour formater les montants.

      Une table de seuils par devise prime sur la conversion, parce que
      les règles d'expédition se saisissent zone par zone dans l'admin
      et que la barre doit annoncer ce que le paiement applique.

   2. LE PANIER QUI CHANGE SANS RECHARGEMENT. Liquid ne s'exécute qu'au
      chargement. On ajoute un article depuis le tiroir, on modifie une
      quantité : sans ce module, la jauge resterait figée.

   3. LE TIROIR QUI SE RECONSTRUIT. C'est le défaut qui a été observé :
      la barre n'apparaissait qu'après rechargement de la page. Shopify
      ne met pas à jour le tiroir panier, il le REMPLACE par du HTML
      fraîchement rendu — donc par un balisage où l'attribut hidden est
      de nouveau posé, puisque Liquid ne connaît ni la devise ni le
      taux. L'initialisation, elle, n'avait tourné qu'une fois au
      chargement de la page, sur des éléments désormais détruits.

      D'où le MutationObserver : tout nouvel élément qui apparaît dans
      le document est traité comme les autres, avec le dernier total
      connu. Un simple écouteur d'événement ne suffit pas — l'ordre
      entre `cart:change` et le remplacement du tiroir n'est pas garanti
      par le thème, et parier dessus reviendrait à réintroduire le même
      défaut sous une autre forme.

   La barre part masquée dans le balisage et n'est révélée que par ce
   module. Si le JavaScript échoue, elle reste invisible : mieux vaut
   une promesse absente qu'une promesse fausse.
   ------------------------------------------------------------------ */
(() => {
  const devise = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const taux = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;

  /* Dernier total connu du panier, en centimes de la devise affichée.
     null tant qu'aucun changement n'a eu lieu : on retombe alors sur la
     valeur rendue par le serveur, qui est juste au chargement. */
  let dernierTotal = null;

  /* « EUR 250, USD 300, MAD 2500 » — séparateurs libres, casse libre.
     On lit ce que le marchand a écrit, pas ce qu'on aimerait qu'il
     écrive : virgules, points-virgules ou retours à la ligne. */
  const lireTable = (brut) => {
    const table = {};
    (brut || '').split(/[,;\n]/).forEach((entree) => {
      const m = entree.trim().match(/^([A-Za-z]{3})\s*[:=\s]\s*([\d.,]+)$/);
      if (m) table[m[1].toUpperCase()] = parseFloat(m[2].replace(',', '.'));
    });
    return table;
  };

  /* L'ARRONDI VA VERS LE HAUT, JAMAIS VERS LE BAS.

     Les deux erreurs ne coûtent pas la même chose. Un seuil affiché
     au-dessus du vrai fait une bonne surprise au paiement : la livraison
     est offerte plus tôt qu'annoncé. Affiché en dessous, il promet une
     gratuité que le paiement refuse, et le panier est abandonné.

     On arrondit à l'unité entière et non au centime : « plus que 272 $ »
     se lit, « plus que 271,43 $ » sent le calcul automatique.

     ATTENTION À L'UNITÉ. Les montants circulent ici en CENTIMES, comme
     partout dans l'API panier de Shopify. L'arrondi se fait donc sur la
     valeur ramenée à l'unité, puis on repasse en centimes — sans cette
     division, on multipliait par cent un montant déjà en centimes et le
     plancher devenait cent fois trop haut, ce qui masquait la barre en
     toutes circonstances. */
  const versLeHaut = (centimes) => Math.ceil(centimes / 100 - 1e-6) * 100;

  const seuilPour = (cadre) => {
    const table = lireTable(cadre.getAttribute('data-seuils-devises'));

    /* Montant saisi à la main : c'est la règle d'expédition réelle, on
       la reprend telle quelle sans y toucher. */
    if (table[devise] > 0) return Math.round(table[devise] * 100);

    return versLeHaut(Number(cadre.getAttribute('data-seuil') || 0) * taux);
  };

  const totalServeur = (cadre) => {
    const composant = cadre.querySelector('free-shipping-bar');
    return composant ? Number(composant.getAttribute('total-price') || 0) : 0;
  };

  const actualiser = (cadre) => {
    const total = dernierTotal === null ? totalServeur(cadre) : dernierTotal;
    const seuil = seuilPour(cadre);
    /* Même arrondi vers le haut : la barre apparaît un rien plus tard,
       ce qui ne coûte rien, plutôt qu'un rien trop tôt sur un panier qui
       ne la mérite pas encore. */
    const plancher = versLeHaut(Number(cadre.getAttribute('data-plancher') || 0) * taux);

    if (!seuil) {
      cadre.hidden = true;
      return;
    }

    cadre.hidden = total < plancher;
    cadre.style.setProperty('--avb-livraison-progres', `${Math.min(100, Math.round((total / seuil) * 100))}%`);

    /* Le composant du thème compare au seuil brut de son attribut : on
       le tient à jour avec le seuil converti, sinon la phrase et la
       barre annonceraient deux montants différents. */
    const composant = cadre.querySelector('free-shipping-bar');
    if (composant) {
      composant.setAttribute('threshold', seuil);
      composant.setAttribute('total-price', total);
    }
  };

  const toutActualiser = () => document.querySelectorAll('[data-avb-livraison]').forEach(actualiser);

  toutActualiser();

  document.addEventListener('cart:change', (event) => {
    const panier = event.detail && event.detail.cart;
    if (!panier) return;

    const aExpedier = (panier.items || [])
      .filter((article) => article.requires_shipping)
      .reduce((somme, article) => somme + article.final_line_price, 0);

    const remises = (panier.cart_level_discount_applications || []).reduce(
      (somme, remise) => somme + remise.discount_application.total_allocated_amount,
      0
    );

    dernierTotal = aExpedier - remises;
    toutActualiser();
  });

  /* Le tiroir panier est remplacé, pas modifié : on traite tout nouvel
     élément dès son apparition dans le document. */
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const noeud of mutation.addedNodes) {
          if (noeud.nodeType !== 1) continue;
          if (noeud.matches && noeud.matches('[data-avb-livraison]')) actualiser(noeud);
          if (noeud.querySelectorAll) noeud.querySelectorAll('[data-avb-livraison]').forEach(actualiser);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ------------------------------------------------------------------
   Marchés en sous-dossier : les liens internes gardent le préfixe.

   LE PROBLÈME. La boutique sert désormais /uk/, /ch/, /gulf/, /no/, /us/
   et /de/. Un lien écrit « /pages/retours » ramène à la racine, c'est-à-
   dire au marché français en euros : une visiteuse britannique qui clique
   sur la FAQ perd son marché, sa devise et sa langue au milieu de son
   parcours. C'est un panier perdu pour un lien mal formé.

   POURQUOI EN JAVASCRIPT ET PAS EN LIQUID. Les liens des fichiers .liquid
   ont été corrigés à la source, c'est toujours préférable. Mais une
   partie du contenu vit dans des réglages de section stockés en JSON —
   la FAQ, le portail de retour — où Liquid n'est jamais évalué : ce qui
   est écrit dans l'éditeur est servi tel quel. Il fallait donc un filet
   qui rattrape ces liens-là, et ceux que l'on écrira demain sans y
   penser.

   CE QUI EST ÉPARGNÉ. Les proxys d'application (/a/, /apps/), les
   fichiers du CDN, le paiement et les services Shopify : ces adresses ne
   se localisent pas, les préfixer les casserait. Les liens déjà préfixés
   et les liens externes ne sont pas touchés non plus.

   LE LIEN VERS L'ACCUEIL EST UN CAS À PART. Shopify expose la racine du
   marché sous deux formes : `Shopify.routes.root` vaut « /en/ », avec
   barre finale, tandis que le Liquid `routes.root_url` écrit « /en »,
   sans. Le test « déjà préfixé » comparait à la forme longue : « /en »
   n'y correspondait pas, et le lien du logo devenait « /en/en » — une
   page 404. Le même sort frappait le fil d'Ariane et l'onglet Accueil de
   l'application.

   On compare donc au préfixe nu, et on accepte les deux formes : le
   préfixe seul, ou le préfixe suivi d'une barre. Écrit ainsi, « /england »
   reste bien préfixé, ce qu'un simple `startsWith('/en')` aurait raté.
   ------------------------------------------------------------------ */
(() => {
  const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (racine === '/') return; /* marché principal : rien à préfixer */

  const PREFIXE = racine.replace(/\/$/, '');
  const EPARGNES = ['/a/', '/apps/', '/cdn/', '/checkout', '/services/', '/wpm@', '//'];

  const corriger = (lien) => {
    const href = lien.getAttribute('href');
    if (!href || href[0] !== '/') return;
    if (href === PREFIXE || href.startsWith(PREFIXE + '/')) return;
    if (EPARGNES.some((debut) => href.startsWith(debut))) return;

    lien.setAttribute('href', PREFIXE + href);
  };

  const tout = (racineDom) => {
    if (racineDom.matches && racineDom.matches('a[href^="/"]')) corriger(racineDom);
    if (racineDom.querySelectorAll) racineDom.querySelectorAll('a[href^="/"]').forEach(corriger);
  };

  tout(document);

  /* Les tiroirs et les résultats de recherche arrivent après coup : on
     traite ce qui apparaît, comme pour la barre de livraison. */
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const noeud of mutation.addedNodes) {
          if (noeud.nodeType === 1) tout(noeud);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ------------------------------------------------------------------
   Fraîcheur de la page collection

   LE PROBLÈME, ET SA MESURE. Une visiteuse arrivant d'une publicité
   TikTok ouvre la page dans le navigateur intégré de l'application, et
   y voit parfois des produits qui ne sont plus dans la collection.

   Relevé sur la boutique : la page ne renvoie AUCUN en-tête de cache.
   Ni Cache-Control, ni Last-Modified, ni Expires. Quand un serveur ne
   dit rien de la durée de validité d'une page, la spécification HTTP
   autorise le navigateur à l'inventer, et les navigateurs intégrés aux
   applications sont réputés généreux dans ce calcul. Le CDN annonce
   DYNAMIC, il ne conserve donc rien : il est hors de cause. Et la cause
   ne peut pas être corrigée à sa racine, Shopify n'exposant pas les
   en-têtes HTTP de la boutique.

   POURQUOI PAS UN BOUTON À CONFIRMER. L'idée d'un panneau obligatoire
   dont la validation rechargerait la page a été écartée pour deux
   raisons. Elle ne vide rien : recharger la même adresse peut resservir
   la même entrée de cache, il aurait fallu de toute façon changer
   l'adresse — et alors le bouton ne servait plus à rien. Et elle coûte
   précisément ce qu'elle prétendait épargner, un écran de plus avant la
   première photo de produit, sur du trafic publicitaire froid.

   CE QUI EST FAIT ICI. Le serveur inscrit l'heure du rendu dans la
   page. Elle ne ment jamais sur son âge : si le script la trouve
   vieille, la page vient d'un cache, quel qu'il soit. Il redemande
   alors cette seule section, sans cache, et ne remplace QUE si la liste
   des produits a réellement changé. Aucun clic, aucun écran
   intermédiaire, et la protection vaut pour toutes les sources de
   trafic — Meta, Pinterest, un lien collé dans une story, un onglet
   resté ouvert deux jours.

   LE SEUIL EST HAUT VOLONTAIREMENT. Shopify tient son propre cache de
   page, court, qui sert la même page à plusieurs visiteuses pendant
   quelques minutes. Un seuil serré déclencherait une requête pour tout
   le monde sans rien corriger. Dix minutes laissent passer ce cache-là
   et attrapent celui qui pose problème, qui se compte en heures.
   ------------------------------------------------------------------ */
(() => {
  const SEUIL_SECONDES = 600;

  /* Dans l'éditeur de thème, Shopify recompose les sections lui-même :
     un remplacement concurrent y ferait plus de mal que de bien. */
  if (window.Shopify && window.Shopify.designMode) return;

  const marqueur = document.querySelector('[data-avb-fraicheur]');
  if (!marqueur) return;

  const rendu = parseInt(marqueur.getAttribute('data-avb-rendu'), 10);
  const identifiant = marqueur.getAttribute('data-avb-section');
  if (!rendu || !identifiant) return;

  const section = document.getElementById(`shopify-section-${identifiant}`);
  if (!section) return;

  const age = Math.floor(Date.now() / 1000) - rendu;
  if (age < SEUIL_SECONDES) return;

  /* L'empreinte est la suite des adresses produit, dans l'ordre. Elle
     change dès qu'un article entre, sort ou se déplace, et ignore tout
     ce qui varie sans conséquence d'un rendu à l'autre — jetons,
     identifiants aléatoires, attributs posés par les applications.
     Comparer les deux balisages bruts aurait donné une différence à
     chaque fois, donc un remplacement systématique. */
  const empreinte = (racine) =>
    Array.from(racine.querySelectorAll('a[href*="/products/"]'))
      .map((lien) => (lien.getAttribute('href') || '').split('?')[0])
      .join('|');

  /* Une grille de cent produits pèse quelques centaines de kilooctets.
     Sur un forfait compté, mieux vaut une collection un peu datée
     qu'une facture : on respecte le mode économie de données. */
  if (navigator.connection && navigator.connection.saveData) return;

  /* DEUX API DE RENDU DE SECTION EXISTENT, ET UNE SEULE MARCHE ICI.

     La récente, `?sections=<id>`, renvoie du JSON. Essayée en premier,
     elle a répondu `null` pour la section principale de la collection —
     la bannière voisine, elle, se rendait normalement. Shopify refuse
     de servir ainsi la section principale d'un gabarit.

     L'ancienne, `?section_id=<id>`, renvoie le balisage brut et
     fonctionne : c'est d'ailleurs celle que le thème utilise pour ses
     propres filtres. L'identifiant attendu est celui du gabarit entier,
     de la forme `template--00000__main` ; il est écrit par Liquid, on
     ne le devine pas. */
  const adresse = new URL(window.location.href);
  adresse.searchParams.set('section_id', identifiant);

  fetch(adresse.toString(), { cache: 'no-store' })
    .then((reponse) => (reponse.ok ? reponse.text() : null))
    .then((balisage) => {
      if (!balisage) return;

      const recu = new DOMParser().parseFromString(balisage, 'text/html');

      /* La réponse porte l'enveloppe `#shopify-section-…` — vérifié sur
         la boutique. On la retrouve par identifiant, puis par classe,
         et à défaut on prend le corps entier : trois filets pour un
         format que Shopify pourrait faire évoluer sans prévenir. */
      const frais =
        recu.getElementById(`shopify-section-${identifiant}`) ||
        recu.querySelector('.shopify-section') ||
        recu.body;
      if (!frais.firstElementChild) return;

      if (empreinte(frais) === empreinte(section)) return;

      section.innerHTML = frais.innerHTML;

      /* Les composants du thème sont des éléments personnalisés : ils
         se réinitialisent seuls en rejoignant le document. Les modules
         qui n'en sont pas ont besoin d'être prévenus. */
      document.dispatchEvent(
        new CustomEvent('avb:section-remplacee', { detail: { section } })
      );
    })
    .catch(() => {
      /* Réseau coupé, réponse illisible : on garde la page telle
         quelle. Une collection périmée reste préférable à une grille
         vide. */
    });
})();

/* ------------------------------------------------------------------
   Diaporama sans effet de transition

   Le diaporama du thème anime chaque changement de diapositive : fondu
   de la sortante, zoom arrière et remontée du texte de l'entrante.
   L'option « Désactiver l'effet » du réglage de section pose l'attribut
   data-avb-sans-effet ; ici, on remplace les deux fabriques
   d'animations de l'élément par des bascules immédiates.

   ON SURCHARGE LES MÉTHODES DE L'INSTANCE, PAS LA CLASSE : les autres
   diaporamas de la boutique (sans l'option) gardent leur effet. Et on
   passe par la définition du composant plutôt que par un sélecteur
   immédiat, car les sections arrivent parfois après ce script.

   createOnBecameVisibleAnimationControls n'est PAS touchée : elle porte
   l'animation d'entrée de la première diapositive, qui a son propre
   réglage (« show_initial_transition ») — deux options, deux effets.
   ------------------------------------------------------------------ */
(() => {
  customElements.whenDefined('slideshow-carousel').then(() => {
    const neutraliser = (carrousel) => {
      if (!carrousel.hasAttribute('data-avb-sans-effet') || carrousel._avbSansEffet) return;
      carrousel._avbSansEffet = true;

      carrousel.createOnChangeAnimationControls = (fromSlide, toSlide) => {
        /* Le thème coupe et relance les vidéos dans sa version animée ;
           on garde ce comportement, sans quoi une vidéo continuerait de
           jouer en coulisse. */
        fromSlide.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          video.pause();
        });
        toSlide.querySelectorAll('video').forEach((video) => {
          if (!video.offsetParent) return;
          video.muted = true;
          video.currentTime = 0;
          video.play().catch(() => {});
        });

        return {
          leaveControls: () => ({ finished: Promise.resolve() }),
          enterControls: () => ({ finished: Promise.resolve() })
        };
      };
    };

    document.querySelectorAll('slideshow-carousel').forEach(neutraliser);

    if ('MutationObserver' in window) {
      new MutationObserver(() => {
        document.querySelectorAll('slideshow-carousel[data-avb-sans-effet]').forEach(neutraliser);
      }).observe(document.body, { childList: true, subtree: true });
    }
  });
})();

/* ------------------------------------------------------------------
   Hauteur du clavier virtuel

   Sur iOS, le clavier ne rétrécit pas la fenêtre : il se pose par-dessus
   elle. Une feuille ancrée en bas d'un cadre plein écran se retrouve donc
   dessous, invisible, alors que rien dans le CSS ne permet de le savoir —
   `dvh` décrit la fenêtre, pas ce qui la recouvre.

   `visualViewport` décrit, lui, ce que l'œil voit réellement. La
   différence avec `innerHeight` est la hauteur prise par le clavier. On
   la publie une fois pour toutes dans une variable, à charge pour le CSS
   de s'en servir.

   Sur Android la fenêtre est réellement rétrécie : la différence vaut
   zéro et la variable reste à zéro. Rien à traiter à part.
   ------------------------------------------------------------------ */
(() => {
  const vue = window.visualViewport;
  if (!vue) return;

  const racine = document.documentElement;

  const mesurer = () => {
    /* offsetTop compte le décalage quand iOS fait remonter la page pour
       dégager le champ actif : sans lui, le clavier serait sous-estimé
       d'autant. */
    const hauteur = Math.max(0, window.innerHeight - vue.height - vue.offsetTop);
    racine.style.setProperty('--avb-clavier', `${Math.round(hauteur)}px`);
  };

  vue.addEventListener('resize', mesurer);
  vue.addEventListener('scroll', mesurer);
  mesurer();
})();

/* ------------------------------------------------------------------
   Recherche dans le sélecteur de pays et de devise

   La boutique livre dans plus de deux cents pays. Chercher le sien dans
   une liste de deux cents lignes sur un téléphone revient à faire
   défiler une page entière, au moment précis où la visiteuse veut
   simplement voir les prix dans sa monnaie.

   LE CHAMP EST RÉVÉLÉ PAR LE SCRIPT, PAS PAR LE BALISAGE. Il part
   masqué : sans JavaScript, un champ de recherche qui ne filtre rien
   serait pire que pas de champ du tout. La liste complète, elle, reste
   utilisable dans tous les cas.

   ON FILTRE SUR LE NOM ET SUR LA DEVISE. Une visiteuse tape « USD »
   aussi souvent que « United States » — surtout si elle ne connaît pas
   le nom anglais de son pays. Les deux sont préparés côté Liquid dans
   un attribut, en minuscules : comparer des chaînes déjà normalisées
   évite de le refaire à chaque frappe sur deux cents éléments.

   Les accents sont retirés des deux côtés : « Suède » se trouve en
   tapant « suede », ce que fait n'importe qui sur un clavier pressé.
   ------------------------------------------------------------------ */
(() => {
  const sansAccent = (texte) =>
    texte.normalize('NFD').replace(/[̀-ͯ]/g, '');

  document.querySelectorAll('[data-avb-recherche-pays]').forEach((cadre) => {
    const champ = cadre.querySelector('[data-avb-recherche-pays-champ]');
    const liste = cadre.parentElement?.querySelector('[data-avb-liste-pays]');
    const vide = cadre.parentElement?.querySelector('[data-avb-recherche-pays-vide]');
    if (!champ || !liste) return;

    const options = [...liste.querySelectorAll('[data-avb-cherchable]')];
    if (options.length < 12) return; /* une liste courte se lit plus vite qu'elle ne se filtre */

    /* Le texte cherchable est normalisé une fois pour toutes, pas à
       chaque frappe : deux cents normalisations par caractère tapé se
       sentiraient sur un téléphone d'entrée de gamme. */
    const index = options.map((option) => ({
      element: option,
      texte: sansAccent(option.getAttribute('data-avb-cherchable') || ''),
    }));

    cadre.hidden = false;

    champ.addEventListener('input', () => {
      const requete = sansAccent(champ.value.trim().toLowerCase());
      let visibles = 0;

      index.forEach(({ element, texte }) => {
        const garde = requete === '' || texte.includes(requete);
        element.hidden = !garde;
        if (garde) visibles += 1;
      });

      if (vide) vide.hidden = visibles > 0;
    });

    /* Le popover se rouvre sur son état précédent : on repart d'une
       liste entière plutôt que du filtre de la dernière visite, qui
       donnerait l'impression que des pays ont disparu. */
    const reinitialiser = () => {
      champ.value = '';
      index.forEach(({ element }) => (element.hidden = false));
      if (vide) vide.hidden = true;
    };

    const popover = cadre.closest('x-popover');
    popover?.addEventListener('toggle', reinitialiser);
    popover?.addEventListener('close', reinitialiser);
  });
})();

/* ------------------------------------------------------------------
   Pastille panier de la barre d'onglets (application)

   Le nombre est tenu à jour par l'élément cart-count du thème ; seul
   l'écrin doit apparaître et disparaître — une pastille affichant zéro
   serait pire que pas de pastille.
   ------------------------------------------------------------------ */
(() => {
  document.addEventListener('cart:change', (event) => {
    const compte = event.detail?.cart?.item_count;
    if (typeof compte !== 'number') return;
    document.querySelectorAll('[data-avb-tab-panier]').forEach((badge) => {
      badge.hidden = compte === 0;
    });
  });
})();

/* ------------------------------------------------------------------
   Recherche depuis le tiroir menu

   LE TIROIR DOIT SE REFERMER D'ABORD. La fenêtre de recherche est un
   dialogue comme le tiroir, et deux dialogues empilés s'ordonnent par
   leur ordre d'apparition dans la page : celle-ci s'ouvrait donc
   DERRIÈRE le tiroir, visible mais hors d'atteinte — on ne pouvait
   rien saisir.

   Se battre à coups de z-index aurait donné deux couches superposées
   ouvertes en même temps, ce qui n'a aucun sens : on quitte le menu
   pour aller chercher. On referme, puis on ouvre. Les deux méthodes du
   thème renvoient une promesse, l'enchaînement est donc exact et non
   pas minuté au hasard.

   Sans JavaScript, ou si la fenêtre est absente de la page, le lien
   reste un lien et mène à la page de recherche.
   ------------------------------------------------------------------ */
(() => {
  document.addEventListener('click', async (event) => {
    const lien = event.target.closest('[data-avb-recherche]');
    if (!lien) return;

    /* On laisse passer les clics qui ouvrent un onglet : la personne a
       demandé autre chose que ce qu'on s'apprête à faire. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    /* L'attribut peut être vide : la barre d'onglets de l'application
       et le champ de l'accueil sont rendus hors de la section d'en-tête
       et n'en connaissent pas l'identifiant. On retrouve alors la
       fenêtre de recherche par son préfixe — il n'y en a qu'une. */
    /* Le sélecteur est qualifié par la balise : un <template> du thème
       porte un identifiant au même préfixe et arrivait premier dans le
       document — sans méthode show, le garde-fou laissait la
       navigation classique s'exécuter. C'était le bug du champ
       d'accueil qui menait à la page /search au lieu d'ouvrir la
       fenêtre. */
    const cible = lien.getAttribute('data-avb-recherche');
    const fenetre = cible
      ? document.getElementById(cible)
      : document.querySelector('header-search[id^="header-search-"]');
    if (!fenetre || typeof fenetre.show !== 'function') return;

    event.preventDefault();

    const tiroir = lien.closest('header-sidebar');
    if (tiroir && typeof tiroir.hide === 'function') {
      await tiroir.hide();
    }

    fenetre.show();
  });
})();

/* ------------------------------------------------------------------
   Indicateur de défilement du tiroir menu

   Le menu dépasse la hauteur de l'écran sur un téléphone : collections
   du bas, liens de service et bloc communauté sont hors champ à
   l'ouverture. Rien ne le disait, et un menu qui semble complet ne
   donne aucune raison de faire défiler.

   POURQUOI LE DESSINER PLUTÔT QU'UTILISER LA BARRE NATIVE. Sur iOS la
   barre système n'apparaît qu'en cours de défilement — donc jamais au
   moment où l'information serait utile, c'est-à-dire à l'ouverture.

   L'ÉLÉMENT EST CRÉÉ ICI, PAS DANS LE BALISAGE. Le tiroir est cloné
   depuis un <template> à la première ouverture : un élément écrit dans
   le Liquid y serait dupliqué à chaque fois. On le pose une seule
   fois, au premier affichage, et on s'en tient là.
   ------------------------------------------------------------------ */
(() => {
  const poser = (zone) => {
    if (!zone || zone.querySelector('.avb-menu-defilement')) return;

    const barre = document.createElement('div');
    barre.className = 'avb-menu-defilement';
    barre.setAttribute('aria-hidden', 'true');

    const jauge = document.createElement('span');
    jauge.className = 'avb-menu-defilement__jauge';
    barre.appendChild(jauge);
    zone.prepend(barre);

    const actualiser = () => {
      /* LA BARRE COMMENCE SOUS LA RECHERCHE. Elle longeait toute la
         zone de défilement, donc aussi le lien de recherche posé en
         tête — elle semblait appartenir à la liste des collections
         alors qu'elle rend compte du menu entier. Le décalage est
         mesuré, pas estimé : la hauteur du lien varie avec la langue
         et la taille de police du système. */
      const entete = zone.querySelector('.avb-sidebar-recherche');
      const decalage = entete ? entete.offsetHeight + entete.offsetTop : 0;
      barre.style.setProperty('--avb-menu-haut', `${decalage}px`);

      const total = zone.scrollHeight;
      const visible = zone.clientHeight;

      /* Marge de huit pixels : un contenu qui dépasse de deux ou trois
         pixels à cause d'un arrondi n'est pas un contenu à faire
         défiler, et la barre clignoterait pour rien. */
      if (total - visible < 8) {
        barre.removeAttribute('data-visible');
        return;
      }

      barre.setAttribute('data-visible', '');

      const part = Math.max(12, (visible / total) * 100);
      const avancement = zone.scrollTop / (total - visible);
      barre.style.setProperty('--avb-menu-part', `${part}%`);
      barre.style.setProperty('--avb-menu-position', `${avancement * (100 / part) * 100 - avancement * 100}%`);
    };

    zone.addEventListener('scroll', actualiser, { passive: true });

    /* CE QUI CHANGE, C'EST LE CONTENU, PAS LE CADRE. Observer la seule
       zone de défilement ne suffisait pas : sa hauteur est fixée par
       l'écran et ne bouge jamais. Quand on repliait « Nos collections »,
       le contenu redevenait plus court que l'écran mais l'indicateur
       restait affiché, à annoncer un défilement qui n'existait plus.

       On observe donc aussi les enfants — c'est leur hauteur qui varie
       — et on écoute l'ouverture des accordéons. L'événement `toggle`
       ne remonte pas jusqu'à la zone : il faut le capter à la descente,
       d'où le troisième argument. */
    if ('ResizeObserver' in window) {
      const observateur = new ResizeObserver(actualiser);
      observateur.observe(zone);
      Array.from(zone.children).forEach((enfant) => {
        if (enfant !== barre) observateur.observe(enfant);
      });
    }

    /* L'accordéon s'ouvre en animant sa hauteur : au moment du `toggle`
       la hauteur finale n'est pas encore atteinte, et une mesure prise
       tout de suite donnerait l'état d'avant. On mesure à la fin de
       l'animation, et une fois à la frame suivante pour les navigateurs
       qui n'animent pas. */
    zone.addEventListener('toggle', () => {
      requestAnimationFrame(actualiser);
    }, true);

    zone.addEventListener('transitionend', actualiser);
    zone.addEventListener('animationend', actualiser);

    actualiser();
  };

  /* Le tiroir n'existe qu'après sa première ouverture : on guette son
     apparition plutôt que de la supposer au chargement. */
  const zoneImmediate = document.querySelector('.header-sidebar__scroller');
  if (zoneImmediate) poser(zoneImmediate);

  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      document.querySelectorAll('.header-sidebar__scroller').forEach(poser);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ------------------------------------------------------------------
   Page de retour : la date de livraison décide du délai.

   LE DÉLAI ÉTAIT UNE LIGNE DANS LES CONDITIONS. Quatorze jours, écrit
   en quatrième position d'une liste que personne ne lit jusqu'au bout.
   Une cliente hors délai l'apprenait à l'arrivée de son colis : un
   transport perdu pour elle, un colis à réexpédier pour nous.

   Elle saisit désormais sa date de livraison avant d'engager quoi que
   ce soit, et la page lui répond.

   LE SENS DE LA DÉFAILLANCE EST CHOISI. Ce module ne fait que RETIRER
   le bouton quand le délai est dépassé ; il ne le révèle jamais. Si le
   script ne s'exécute pas — réseau, bloqueur, erreur ailleurs dans le
   fichier — la page se comporte comme avant son existence et la règle
   écrite s'applique. L'inverse bloquerait toutes les clientes au
   moindre incident, sur la page où elles sont déjà contrariées.

   La date limite est formatée par le navigateur dans la langue de la
   page : une Allemande lit le 27.10.2026, une Française le 27/10/2026.
   ------------------------------------------------------------------ */
(() => {
  const champ = document.querySelector('[data-avb-retour-date]');
  if (!champ) return;

  const verdict = document.querySelector('[data-avb-retour-verdict]');
  const apres = document.querySelector('[data-avb-retour-apres]');
  const limite = document.querySelector('[data-avb-retour-limite]');
  if (!verdict || !apres) return;

  /* La condition de dépôt est rendue sans date : si rien ne tourne,
     elle reste juste. Le script la remplace par sa version datée dès
     que la livraison est connue, et la remet telle quelle si le champ
     est vidé. */
  const limiteSansDate = limite ? limite.textContent : '';

  const JOURS = 14;
  const JOUR_MS = 86400000;

  /* On compare des jours, pas des instants : une livraison du 1er à
     18 h et une consultation du 15 à 9 h font bien quatorze jours
     pleins. Travailler sur les horodatages bruts ferait basculer le
     verdict selon l'heure de la journée. */
  const aMinuit = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());

  const juger = () => {
    const valeur = champ.value;

    if (!valeur) {
      verdict.hidden = true;
      apres.hidden = true;
      if (limite) limite.textContent = limiteSansDate;
      return;
    }

    const [a, m, j] = valeur.split('-').map(Number);
    const livraison = new Date(a, m - 1, j);
    if (Number.isNaN(livraison.getTime())) return;

    /* `dateLimite`, et surtout pas `limite` : ce nom désigne déjà le
       <li> de la condition de dépôt, déclaré plus haut. Une seconde
       déclaration dans cette fonction l'éclipsait entièrement — le
       verdict s'affichait, puis `limite.dataset` levait une erreur sur
       un objet Date et la suite de la fonction ne s'exécutait jamais.
       L'étape 3 restait repliée. */
    const dateLimite = new Date(a, m - 1, j + JOURS);
    const ecoules = Math.round((aMinuit(new Date()) - aMinuit(livraison)) / JOUR_MS);
    const dansLesTemps = ecoules >= 0 && ecoules <= JOURS;

    verdict.hidden = false;
    verdict.classList.toggle('avb-retour__verdict--ko', !dansLesTemps);

    if (dansLesTemps) {
      let date;
      try {
        date = dateLimite.toLocaleDateString(document.documentElement.lang || undefined);
      } catch (erreur) {
        date = dateLimite.toLocaleDateString();
      }
      verdict.textContent = (champ.dataset.ok || '').replace('%DATE%', date);
      if (limite) limite.textContent = (limite.dataset.tpl || '').replace('%DATE%', date) || limiteSansDate;
      apres.hidden = false;
    } else {
      verdict.textContent = champ.dataset.ko || '';
      apres.hidden = true;
    }
  };

  champ.addEventListener('change', juger);
  champ.addEventListener('input', juger);

  /* Premier repli au chargement, et non en CSS : c'est ce qui garde
     la page utilisable si ce fichier ne s'exécute jamais. Le champ
     peut déjà porter une valeur — un retour en arrière du navigateur
     restitue la saisie — d'où un jugement immédiat plutôt qu'un
     masquage aveugle. */
  juger();
})();
